import {
  CardLoading,
  CardNotice,
} from '@euler/app/flows/ai-suite/components/ChatCard';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiUploadedDatasetView } from '@euler/model/bi/dataset';
import { BiDatasetRef } from '@euler/model/bi/query';
import { memo, useEffect } from 'react';
import { View } from 'react-native';
import { BiRetry } from '../../components/ui/BiRetry';
import { DatasetSection } from './DatasetSection';
import { DimensionsSection } from './DimensionsSection';
import { FiltersSection } from './FiltersSection';
import { MeasuresSection } from './MeasuresSection';
import { SortLimitSection } from './SortLimitSection';
import { TimeRangeSection } from './TimeRangeSection';
import { BiEditorQuery } from './functions/biEditorState';
import { BiDatasetCatalogState } from './functions/useBiDatasetCatalog';

/**
 * One query's part of the form: its dataset, then — once its catalog is
 * read — measures, dimensions (not in a blend, where the merge keys set
 * them), time range, filters, sort and limit.
 */
export const QuerySections = memo(
  ({
    query,
    catalog,
    builtin,
    uploaded,
    datasetTitle,
    datasetNote,
    exclude,
    withDimensions,
    withSortLimit = true,
    onPick,
    onChange,
    onRetryUploads,
    onRetryCatalog,
  }: {
    query: BiEditorQuery;
    catalog: BiDatasetCatalogState;
    builtin: BiDatasetCatalog[];
    uploaded: BiUploadedDatasetView[] | null;
    datasetTitle?: string;
    datasetNote?: string;
    exclude?: BiDatasetRef | null;
    withDimensions: boolean;
    /** Off for a total, which is one row. */
    withSortLimit?: boolean;
    onPick: (dataset: BiDatasetRef) => void;
    onChange: (next: BiEditorQuery) => void;
    onRetryUploads: () => void;
    onRetryCatalog: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const ready = catalog.kind === 'ready' ? catalog.catalog : null;
    // A range kept from another dataset would be sent, unseen, to one with
    // no time field and refused.
    const strayRange = !!ready && !ready.primaryTime && !!query.timeRange;
    useEffect(() => {
      if (strayRange) onChange({ ...query, timeRange: undefined });
    }, [strayRange, query, onChange]);
    return (
      <>
        <DatasetSection
          title={datasetTitle}
          note={datasetNote}
          dataset={query.dataset}
          builtin={builtin}
          uploaded={uploaded}
          current={ready}
          exclude={exclude}
          onPick={onPick}
          onRetryUploads={onRetryUploads}
        />
        {catalog.kind === 'loading' ? (
          <CardLoading label="正在读取字段…" />
        ) : catalog.kind === 'failed' ? (
          <View style={{ gap: rpx(8) }}>
            <CardNotice tone="danger" text={catalog.message} />
            {catalog.retryable ? (
              <BiRetry label="重新读取" onRetry={onRetryCatalog} />
            ) : null}
          </View>
        ) : null}
        {ready ? (
          <>
            <MeasuresSection
              query={query}
              catalog={ready}
              onChange={onChange}
            />
            {withDimensions ? (
              <DimensionsSection
                query={query}
                catalog={ready}
                onChange={onChange}
              />
            ) : null}
            {ready.primaryTime ? (
              <TimeRangeSection query={query} onChange={onChange} />
            ) : null}
            <FiltersSection query={query} catalog={ready} onChange={onChange} />
            {withSortLimit ? (
              <SortLimitSection
                query={query}
                catalog={ready}
                onChange={onChange}
              />
            ) : null}
          </>
        ) : null}
      </>
    );
  },
);
